import { PlusOutlined } from '@ant-design/icons';
import PageToolbar from 'ui/PageToolbar/PageToolbar';
import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Button, Tag, Tooltip, Typography } from 'antd';
import moment from 'moment';
// API
import { GET_CB_PROMO_CODES } from 'api/cbPromocodes/queries';
// Types
import {
  SortDirection,
  CbPromoCodeKind,
  GetCbPromoCodesInput,
} from 'api/graphql-global-types';
import {
  GetCbPromoCodes,
  GetCbPromoCodesVariables,
} from 'api/cbPromocodes/types/GetCbPromoCodes';
import { SortedInfo, TableFilter } from 'ui/Table';
// UI
import Table from 'ui/Table';
// Components
import Actions from './components/Actions/Actions';
import CreateCbPromoCodeModal from './components/CreateCbPromoCodeModal/CreateCbPromoCodeModal';
// Helpers
import {
  CbPromoCode,
  getPromoCodeKindLabel,
  getPromoCodeValueLabel,
  getPromoCodeAnalyticsLabel,
  formatRemainingUses,
} from 'helpers/cbPromoCodes/helpers';
import {
  CB_PROMO_CODE_KIND_LABELS,
  CB_PROMO_CODE_STATUS_COLORS,
} from 'helpers/cbPromoCodes/constants';

import './styles.scss';

const { Text } = Typography;

const kindFilters = Object.values(CbPromoCodeKind).map((value) => ({
  text: CB_PROMO_CODE_KIND_LABELS[value],
  value,
}));

const activeFilters = [
  { text: 'Active', value: true },
  { text: 'Paused', value: false },
];

const CbPromoCodes: React.FC = () => {
  const [pageSize, setPageSize] = useState<number>(10);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [sort] = useState<SortedInfo<string>>({
    order: SortDirection.DESC,
    key: 'createdAt',
  });
  const [searchFilters, setSearchFilters] = useState<TableFilter[]>([]);
  const [createModalVisible, setCreateModalVisible] = useState<boolean>(false);

  const getInput = (): GetCbPromoCodesInput => {
    const input: GetCbPromoCodesInput = {
      direction: sort.order,
      limit: pageSize,
      offset: (currentPage > 1 ? currentPage - 1 : 0) * pageSize,
    };

    searchFilters?.forEach(({ key, value }) => {
      if (key === 'code') {
        input.code = value as string;
      } else if (key === 'kind') {
        input.kind = value as CbPromoCodeKind;
      } else if (key === 'isActive') {
        input.isActive = value as boolean;
      }
    });

    return input;
  };

  const { data, loading } = useQuery<GetCbPromoCodes, GetCbPromoCodesVariables>(
    GET_CB_PROMO_CODES,
    {
      fetchPolicy: 'network-only',
      variables: { input: getInput() },
    }
  );

  const columns = [
    {
      title: 'Code',
      dataIndex: 'code',
      key: 'code',
      fixed: 'left' as const,
      width: 140,
      withSearch: true,
      forceSearchRender: true,
      render: (_: string, record: CbPromoCode) => (
        <Text strong>{record.code || '-'}</Text>
      ),
    },
    {
      title: 'Type',
      dataIndex: 'kind',
      key: 'kind',
      width: 150,
      withRadioFilters: true,
      filters: kindFilters,
      render: (_: CbPromoCodeKind, record: CbPromoCode) =>
        record.kind ? getPromoCodeKindLabel(record.kind) : '-',
    },
    {
      title: 'Value',
      dataIndex: 'value',
      key: 'value',
      width: 160,
      render: (_: unknown, record: CbPromoCode) =>
        getPromoCodeValueLabel(record),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (_: unknown, record: CbPromoCode) =>
        record.status ? (
          <Tag color={CB_PROMO_CODE_STATUS_COLORS[record.status]}>
            {record.status}
          </Tag>
        ) : (
          '-'
        ),
    },
    {
      title: 'Uses',
      dataIndex: 'usedCount',
      key: 'usedCount',
      width: 140,
      render: (_: unknown, record: CbPromoCode) => formatRemainingUses(record),
    },
    {
      title: 'Per acct',
      dataIndex: 'perAccountLimit',
      key: 'perAccountLimit',
      width: 80,
      align: 'center' as const,
      render: (value: number | null | undefined) => value ?? '-',
    },
    {
      title: 'Analytics',
      dataIndex: 'analytics',
      key: 'analytics',
      width: 140,
      render: (_: unknown, record: CbPromoCode) =>
        getPromoCodeAnalyticsLabel(record),
    },
    {
      title: 'Note',
      dataIndex: 'note',
      key: 'note',
      width: 180,
      render: (value: string | null) =>
        value ? (
          <Tooltip title={value}>
            <Text ellipsis style={{ maxWidth: 160 }}>
              {value}
            </Text>
          </Tooltip>
        ) : (
          '-'
        ),
    },
    {
      title: 'Active',
      dataIndex: 'isActive',
      key: 'isActive',
      width: 70,
      align: 'center' as const,
      withRadioFilters: true,
      filters: activeFilters,
      render: (isActive: boolean) =>
        isActive ? <Tag color="green">Yes</Tag> : <Tag color="default">No</Tag>,
    },
    {
      title: 'Created',
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 120,
      render: (value: string) =>
        value ? moment(value).format('MMM DD YYYY') : '-',
    },
    {
      title: 'Actions',
      dataIndex: 'id',
      key: 'id',
      fixed: 'right' as const,
      align: 'center' as const,
      width: 190,
      render: (_: string, record: CbPromoCode) => (
        <Actions promoCode={record} />
      ),
    },
  ];

  return (
    <div className="cb-promo-codes">
      <PageToolbar>
        <Tooltip title="Codes are never deleted — retire them by pausing.">
          <Button
            icon={<PlusOutlined />}
            type="primary"
            onClick={() => setCreateModalVisible(true)}
          >
            Create promo code
          </Button>
        </Tooltip>
      </PageToolbar>

      <Table<CbPromoCode, string>
        columns={columns}
        data={data?.getCbPromoCodes.entities}
        loading={loading}
        total={data?.getCbPromoCodes.total}
        currentPage={currentPage}
        pageSize={pageSize}
        setPageSize={setPageSize}
        setCurrentPage={setCurrentPage}
        searchFilters={searchFilters}
        setSearchFilters={setSearchFilters}
        scroll={{ x: 1480 }}
      />

      {createModalVisible && (
        <CreateCbPromoCodeModal
          show={createModalVisible}
          onCancel={() => setCreateModalVisible(false)}
        />
      )}
    </div>
  );
};

export default CbPromoCodes;
